import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { fmtRelative } from "../components/ui";

export const Route = createFileRoute("/jobs/")({
  component: JobsList,
});

function JobsList() {
  const { t, i18n } = useTranslation();
  const q = useQuery({
    queryKey: ["jobs"],
    queryFn: () => api.listJobs(),
    refetchInterval: (qq) => (qq.state.data?.some((j) => j.status === "running") ? 2000 : 8000),
  });

  return (
    <div className="mx-auto max-w-[1180px] space-y-4 px-6 py-10">
      <div className="flex items-baseline justify-between">
        <h1 className="font-heading text-2xl">{t("jobs.title")}</h1>
        <Link to="/" className="text-sm text-a2 hover:underline">{t("jobs.newResearch")} →</Link>
      </div>

      {q.isLoading && <p className="text-sm text-ink/40">{t("common.loading")}</p>}
      {q.error && <p className="text-sm text-a1">{t("jobs.loadFailed", { error: String(q.error) })}</p>}

      {q.data && q.data.length === 0 && (
        <div className="re-card text-center text-sm text-ink/60">
          {t("jobs.empty")}
        </div>
      )}

      {q.data && q.data.length > 0 && (
        <ul className="space-y-2">
          {q.data.map((j) => (
            <li key={j.id}>
              <Link
                to="/jobs/$id"
                params={{ id: j.id }}
                className="re-card flex items-center justify-between gap-3 hover:-translate-y-0.5 hover:shadow"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <StatusPill status={j.status} />
                    <span className="text-ink/40">{fmtRelative(new Date(j.startedAt).toISOString(), i18n.language)}</span>
                  </div>
                  <div className="mt-1 truncate font-mono text-sm">{j.target || t("jobs.noTarget")}</div>
                </div>
                <code className="hidden font-mono text-xs text-ink/40 md:block">{j.id}</code>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const { t } = useTranslation();
  const color =
    status === "running" ? "bg-a2/15 text-a2" :
    status === "done" ? "bg-ink/10 text-ink" :
    status === "killed" ? "bg-ink/10 text-ink/50" :
    "bg-a1/15 text-a1";
  const label = t(`job.status${status.charAt(0).toUpperCase()}${status.slice(1)}`);
  return <span className={`rounded px-2 py-0.5 font-mono text-xs ${color}`}>{label}</span>;
}
